import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Divider,
  Paper,
  Stack,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@iconify/icons-eva/search-fill';
import ExpandMoreIcon from '@iconify/icons-eva/arrow-ios-downward-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import copyFill from '@iconify/icons-eva/copy-fill';
import CloseIcon from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import { getIcon } from 'src/utils/utility';
import { fDateTime } from 'src/utils/formatTime';
import { checkUPITransactionStatus } from 'src/services/transactionStatusService';
import Page from '../components/Page';
import Logo from '../components/Logo';

const RootStyle = styled(Page)(({ theme }) => ({
  minHeight: '100vh',
  background: '#ffffff',
  padding: theme.spacing(1),
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
}));

const SearchCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 800,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(2, 'auto'),
  boxShadow: theme.shadows[8],
  borderRadius: theme.shape.borderRadius * 2,
  background: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 600,
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 800,
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& .logo': {
    maxWidth: 150,
    height: 'auto',
    '& img': {
      width: '100%',
      height: 'auto',
      maxHeight: 50,
    }
  },
  [theme.breakpoints.up('sm')]: {
    '& .logo': {
      maxWidth: 180,
      '& img': {
        maxHeight: 60,
      }
    }
  },
  [theme.breakpoints.up('md')]: {
    '& .logo': {
      maxWidth: 200,
      '& img': {
        maxHeight: 70,
      }
    }
  }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0 0`,
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'left',
  color: theme.palette.text.secondary,
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: '8px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#e9ecef',
    borderColor: '#dee2e6',
  },
}));

const GridLabel = ({ label, value, color, xs, sm, md, children }) => (
  <Grid item xs={xs} sm={sm} md={md} sx={{ display: 'flex' }}>
    <Item sx={{ width: '100%', height: '100%' }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "80px",
          minHeight: "80px",
        }}
      >
        <h4 style={{ 
          margin: "0 0 4px 0", 
          fontSize: "0.75rem", 
          fontWeight: 600, 
          color: "#666",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          lineHeight: "1.1",
          flex: "0 0 auto"
        }}>
          {label}
        </h4>
        <div style={{ 
          flex: "1 1 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }}>
          {children ? (
            children
          ) : (
            <p style={{ 
              color: color ?? "#1976d2", 
              margin: 0,
              fontSize: "1.1rem",
              fontWeight: 700,
              wordBreak: "break-word",
              lineHeight: "1.2"
            }}>
              {value || "--"}
            </p>
          )}
        </div>
      </div>
    </Item>
  </Grid>
);

export default function CheckTransactionStatus() {
  const theme = useTheme();
  const [upiId, setUpiId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSearch = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!upiId.trim()) {
      setError('Please enter a valid UPI ID');
      return;
    }

    setLoading(true);
    setError('');
    setPayments([]);
    setSummary(null);

    try {
      const data = await checkUPITransactionStatus(upiId.trim());
      
      // Handle payments array from API response
      if (data && data.data && data.data.payments && Array.isArray(data.data.payments)) {
        setPayments(data.data.payments);
        setSummary(data.data.summary);
      } else if (data && data.payments && Array.isArray(data.payments)) {
        setPayments(data.payments);
        setSummary(data.summary);
      } else {
        setError('No payments found for this UPI ID');
      }
    } catch (err) {
      console.error('API error:', err);
      setError(err.message || 'Failed to fetch payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch(event);
    }
  };

  const handlePaymentClick = (payment) => {
    setSelectedPayment(payment);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPayment(null);
  };



  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'captured':
      case 'processed':
      case 'success':
      case 'completed':
        return 'success';
      case 'refunded':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'captured':
      case 'processed':
      case 'success':
      case 'completed':
        return checkmarkCircle2Fill;
      case 'refunded':
        return closeCircleFill;
      case 'failed':
      case 'cancelled':
      case 'error':
        return closeCircleFill;
      default:
        return clockFill;
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0.00';
    const amountInRupees = Number(amount) / 100;
    return `₹${amountInRupees.toFixed(2)}`;
  };

  const formatSummaryAmount = (amount) => {
    if (!amount) return '₹0.00';
    const amountInRupees = Number(amount);
    return `₹${amountInRupees.toFixed(2)}`;
  };

  return (
    <RootStyle title="Check Transaction Status">
      <SearchCard>
        <CardContent sx={{ p: 4 }}>
          <LogoContainer>
            <Box className="logo">
              <Logo />
            </Box>
          </LogoContainer>
          
          <Box textAlign="center" mb={4}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              fontWeight="bold" 
              color="text.primary"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              Check Transaction Status
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
              }}
            >
              Enter your UPI ID to view transaction and payment information
            </Typography>
          </Box>

          <Box sx={{ 
            maxWidth: { xs: '100%', sm: 500, md: 600 }, 
            mx: 'auto',
            width: '100%'
          }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="UPI ID"
                placeholder="user@paytm, user@phonepe, user@googlepay"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                helperText="Enter your UPI ID (e.g., user@paytm)"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />

              <Box textAlign="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSearch}
                  disabled={loading || !upiId.trim()}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : getIcon(SearchIcon)}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minWidth: { xs: 120, sm: 140 }
                  }}
                >
                  {loading ? 'Searching...' : 'Check Status'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </SearchCard>

      {/* Payments Table */}
      {payments.length > 0 && (
        <Box sx={{ 
          mt: 4, 
          maxWidth: { xs: '100%', sm: 600, md: 1200 }, 
          mx: 'auto',
          width: '100%',
          px: { xs: 1, sm: 2 }
        }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                background: 'linear-gradient(135deg, #4A90E2 0%, #7DD3FC 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}
            >
              Payments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              UPI ID: {upiId}
            </Typography>
          </Box>

          {/* Summary Cards */}
          {summary && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledCard>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {summary.totalPayments || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Payments
                    </Typography>
                  </StyledCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledCard>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {formatSummaryAmount(summary.totalAmount || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </StyledCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledCard>
                    <Typography variant="h6" color="error.main" fontWeight="bold">
                      {formatSummaryAmount(summary.totalRefunded || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Refunded
                    </Typography>
                  </StyledCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StyledCard>
                    <Typography variant="h6" color="info.main" fontWeight="bold">
                      {summary.successRate || '0.00'}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                  </StyledCard>
                </Grid>
              </Grid>
            </Box>
          )}

          <StyledCard>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Payment ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>VPA</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment, index) => (
                    <TableRow 
                      key={payment.paymentId || index}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                      onClick={() => handlePaymentClick(payment)}
                    >
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {payment.paymentId || '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatAmount(payment.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status || '-'}
                          color={getStatusColor(payment.status)}
                          icon={<Icon icon={getStatusIcon(payment.status)} width={16} height={16} />}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
                          {payment.method || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {payment.vpa || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {payment.createdAt ? fDateTime(payment.createdAt) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <Icon icon={ExpandMoreIcon} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledCard>
        </Box>
      )}

      {/* Payment Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'primary.main',
          color: 'white'
        }}>
          <Typography variant="h6" fontWeight="bold">
            Payment Details
          </Typography>
          <IconButton 
            onClick={handleCloseDialog}
            sx={{ color: 'white' }}
            size="small"
          >
            <Icon icon={CloseIcon} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedPayment && (
            <Box>
              {/* Payment Information */}
              <StyledCard sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Payment Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Payment ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {selectedPayment.paymentId || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatAmount(selectedPayment.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.currency || 'INR'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedPayment.status || '-'}
                      color={getStatusColor(selectedPayment.status)}
                      icon={<Icon icon={getStatusIcon(selectedPayment.status)} width={16} height={16} />}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'uppercase' }}>
                      {selectedPayment.method || 'UPI'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      VPA
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.vpa || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.email || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Contact
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.contact || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.createdAt ? fDateTime(selectedPayment.createdAt) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Captured At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.capturedAt ? fDateTime(selectedPayment.capturedAt) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPayment.description || '-'}
                    </Typography>
                  </Grid>
                </Grid>

              </StyledCard>

              {/* Refunds Table */}
              {selectedPayment.refunds && selectedPayment.refunds.length > 0 && (
                <StyledCard>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Refund History
                  </Typography>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Refund ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>UTR / RRN</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPayment.refunds.map((refund, refundIndex) => (
                          <TableRow key={refund.refundId || refundIndex}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {refund.refundId || '-'}
                                </Typography>
                                {refund.refundId && (
                                  <Tooltip title="Copy Refund ID">
                                    <IconButton
                                      size="small"
                                      onClick={() => navigator.clipboard.writeText(refund.refundId)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <Icon icon={copyFill} width={16} height={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                                {formatAmount(refund.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={refund.status || '-'}
                                color={getStatusColor(refund.status)}
                                icon={<Icon icon={getStatusIcon(refund.status)} width={16} height={16} />}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {refund?.acquirerData?.rrn || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {refund.createdAt ? fDateTime(refund.createdAt) : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </StyledCard>
              )}

              {/* No Refunds Message */}
              {(!selectedPayment.refunds || selectedPayment.refunds.length === 0) && (
                <StyledCard>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Refund History
                  </Typography>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No refunds found for this payment
                    </Typography>
                  </Box>
                </StyledCard>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ 
          mt: 3, 
          borderRadius: 2, 
          maxWidth: { xs: '100%', sm: 600, md: 800 }, 
          mx: 'auto',
          width: '100%'
        }}>
          {error}
        </Alert>
      )}


    </RootStyle>
  );
}
